import { Alert, Collapse, Form, Space, Tabs, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import InputField from "../../../components/common/fields/InputField";
import SelectField from "../../../components/common/fields/SelectField";
import SettingsPanel, { SwitchSetting } from "../../../components/common/SettingsPanel";
import Card from "../../../components/common/Card";
import { pathAPI } from "../../../utils/pathApi";
import WebhookClients from "./WebhookClients";

interface AiChatSettingsValues {
  enabled: boolean;
  bubble_enabled: boolean;
  read_only_tools_enabled: boolean;
  booking_enabled: boolean;
  enabled_country_ids: number[];
  enabled_platforms: string[];
  enabled_app_versions: string[];
  disabled_message: string;
}

interface AiChatSettingsFormValues
  extends Omit<AiChatSettingsValues, "enabled_country_ids" | "enabled_app_versions"> {
  enabled_country_ids: string;
  enabled_app_versions: string;
}

interface OwnerAiChatSettingsValues {
  enabled: boolean;
  voice_enabled: boolean;
  disabled_message: string;
}

const DISABLED_MESSAGE_MAX_LENGTH = 240;

const USER_SWITCHES: SwitchSetting[] = [
  { name: "enabled", label: "Enable Chat", description: "Turn AI chat on or off for the user app" },
  { name: "bubble_enabled", label: "Show Bubble", description: "Show the floating chat bubble" },
  { name: "read_only_tools_enabled", label: "Read-only Tools", description: "Let the agent look up data without changing it" },
  { name: "booking_enabled", label: "Booking Handoff", description: "Let the agent hand customers off to booking" },
];

const OWNER_SWITCHES: SwitchSetting[] = [
  { name: "enabled", label: "Enable Chat", description: "Turn AI chat on or off for the salon owner app" },
  { name: "voice_enabled", label: "Voice Assistant", description: "Allow voice input with the assistant" },
];

const defaultSettings: AiChatSettingsValues = {
  enabled: false,
  bubble_enabled: false,
  read_only_tools_enabled: true,
  booking_enabled: false,
  enabled_country_ids: [],
  enabled_platforms: [],
  enabled_app_versions: [],
  disabled_message: "AI assistant is temporarily unavailable",
};

const defaultOwnerSettings: OwnerAiChatSettingsValues = {
  enabled: true,
  voice_enabled: true,
  disabled_message: "AI assistant is temporarily unavailable",
};

const unwrap = (response: any) => response?.data?.data ?? response?.data;

const toFormValues = (settings: AiChatSettingsValues): AiChatSettingsFormValues => ({
  ...settings,
  enabled_country_ids: settings.enabled_country_ids?.join(",") ?? "",
  enabled_app_versions: settings.enabled_app_versions?.join(",") ?? "",
});

const toRequestValues = (values: AiChatSettingsFormValues): AiChatSettingsValues => ({
  ...values,
  enabled_country_ids: values.enabled_country_ids
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(Number)
    .filter((item) => Number.isFinite(item) && item > 0),
  enabled_app_versions: values.enabled_app_versions
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean),
});

const AiChatSettings = () => {
  const [userForm] = Form.useForm<AiChatSettingsFormValues>();
  const [ownerForm] = Form.useForm<OwnerAiChatSettingsValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const [userLoading, setUserLoading] = useState(false);
  const [ownerLoading, setOwnerLoading] = useState(false);

  const platformOptions = useMemo(
    () => [
      { label: "iOS", value: "ios" },
      { label: "Android", value: "android" },
    ],
    []
  );

  const fetchUserSettings = useCallback(async () => {
    setUserLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.aiChat}/settings`);
      userForm.setFieldsValue(toFormValues({ ...defaultSettings, ...unwrap(response) }));
    } catch (error) {
      messageApi.error("Failed to load user AI chat settings");
    } finally {
      setUserLoading(false);
    }
  }, [userForm, messageApi]);

  const saveUserSettings = async (values: AiChatSettingsFormValues) => {
    setUserLoading(true);
    try {
      const response = await axiosInstance.put(
        `${pathAPI.aiChat}/settings`,
        toRequestValues(values)
      );
      userForm.setFieldsValue(toFormValues({ ...defaultSettings, ...unwrap(response) }));
      messageApi.success("User AI chat settings saved");
    } catch (error) {
      messageApi.error("Failed to save user AI chat settings");
    } finally {
      setUserLoading(false);
    }
  };

  const fetchOwnerSettings = useCallback(async () => {
    setOwnerLoading(true);
    try {
      const response = await axiosInstance.get(`${pathAPI.aiChat}/owner-settings`);
      ownerForm.setFieldsValue({ ...defaultOwnerSettings, ...unwrap(response) });
    } catch (error) {
      messageApi.error("Failed to load SO AI chat settings");
    } finally {
      setOwnerLoading(false);
    }
  }, [ownerForm, messageApi]);

  const saveOwnerSettings = async (values: OwnerAiChatSettingsValues) => {
    setOwnerLoading(true);
    try {
      const response = await axiosInstance.put(
        `${pathAPI.aiChat}/owner-settings`,
        values
      );
      ownerForm.setFieldsValue({ ...defaultOwnerSettings, ...unwrap(response) });
      messageApi.success("SO AI chat settings saved");
    } catch (error) {
      messageApi.error("Failed to save SO AI chat settings");
    } finally {
      setOwnerLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSettings();
    fetchOwnerSettings();
  }, [fetchUserSettings, fetchOwnerSettings]);

  const tabItems = [
    {
      key: "user",
      label: "App User",
      children: (
        <Form
          form={userForm}
          layout="vertical"
          initialValues={toFormValues(defaultSettings)}
          onFinish={saveUserSettings}
          disabled={userLoading}
          style={{ marginTop: 8 }}
        >
          <SettingsPanel
            title="User app chat"
            description="Global kill switch for the user app chat bubble and agent webhook tools. App User has no package gate yet, so packageEnabled defaults to true."
            loading={userLoading}
            onReload={fetchUserSettings}
            switches={USER_SWITCHES}
          >
            <Form.Item
              name="enabled_country_ids"
              label="Enabled Country IDs"
              extra="Comma-separated Country table IDs. Leave empty to allow all countries."
            >
              <InputField placeholder="1,2,3" />
            </Form.Item>
            <Form.Item
              name="enabled_platforms"
              label="Enabled Platforms"
              extra="Leave empty to allow every platform."
            >
              <SelectField multiple options={platformOptions} placeholder="All platforms" />
            </Form.Item>
            <Form.Item
              name="enabled_app_versions"
              label="Enabled App Versions"
              extra="Comma-separated user app versions. Leave empty to allow all versions."
            >
              <InputField placeholder="1.2.3,1.2.4" />
            </Form.Item>
            <Form.Item name="disabled_message" label="Disabled Message">
              <InputField multiline minRows={3} inputProps={{ maxLength: DISABLED_MESSAGE_MAX_LENGTH }} />
            </Form.Item>
          </SettingsPanel>
        </Form>
      ),
    },
    {
      key: "so",
      label: "App SO",
      children: (
        <Form
          form={ownerForm}
          layout="vertical"
          initialValues={defaultOwnerSettings}
          onFinish={saveOwnerSettings}
          disabled={ownerLoading}
          style={{ marginTop: 8 }}
        >
          <SettingsPanel
            title="Salon owner app chat"
            description="Global kill switch for the Salon Owner (SO) app AI chat assistant. Each package still controls whether AI Chat is available for salons on that package."
            loading={ownerLoading}
            onReload={fetchOwnerSettings}
            switches={OWNER_SWITCHES}
          >
            <Form.Item name="disabled_message" label="Disabled Message">
              <InputField multiline minRows={3} inputProps={{ maxLength: DISABLED_MESSAGE_MAX_LENGTH }} />
            </Form.Item>
          </SettingsPanel>
        </Form>
      ),
    },
    {
      key: "webhook",
      label: "Webhook",
      children: <WebhookClients />,
    },
  ];

  return (
    <Card className="user">
      {contextHolder}
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Space direction="vertical" size={4}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            AI Chat Settings
          </Typography.Title>
          <Typography.Text type="secondary">
            Runtime controls for User App and Salon Owner (SO) App AI chat assistants.
          </Typography.Text>
        </Space>

        <Alert
          type="info"
          showIcon
          message="AI_CHAT_ENABLED must also be enabled in the backend environment. These settings cannot override the deployment kill switch."
        />

        <Collapse
          size="small"
          items={[
            {
              key: "hierarchy",
              label: "How AI chat control works (global vs package)",
              children: (
                <Typography.Text type="secondary">
                  ✓ Global setting (here) = Kill Switch (enables/disables AI Chat for each app)<br />
                  ✓ Package setting (Package Form) = Feature Toggle (enables/disables AI Chat per package)<br />
                  ✓ Final logic: <code>globalEnabled && packageEnabled</code><br />
                  → App User currently has no package setting, so packageEnabled is treated as true until package support is added.<br />
                  → If global is OFF, AI Chat is disabled regardless of package setting.
                </Typography.Text>
              ),
            },
          ]}
        />

        <Tabs defaultActiveKey="user" items={tabItems} />
      </Space>
    </Card>
  );
};

export default AiChatSettings;
